import { createChatTransport } from 'flare-chat-core/transport';
import type { ChatTransport } from 'flare-chat-core/transport';
import type { JsonObject } from 'flare-chat-core/contracts';
import { normalizeMessageRecord, normalizeSessionRecord } from 'flare-chat-core/transport/normalization/api-records';
import { normalizeSessionListWindow } from 'flare-chat-core/contracts/session-list-window';
import { createHostOperationDefinitions } from '../../application/operations/definitions';
import type { HostConfiguration, HostOptions, MessageAPI, SessionAPI, WireMessageWindow } from '../../host/contracts';

const session = (value: unknown) => normalizeSessionRecord(
  value && typeof value === 'object' && 'data' in value ? value.data : value,
);
const sessionOverrides = (api: SessionAPI, defaults: JsonObject): ChatTransport['sessions'] => ({
  list: async ({ query, cursor, sortOrder } = {}, { signal } = {}) => {
    const response = await api.list({ ...defaults, query, cursor, sort_order: sortOrder }, { signal });
    return { items: (response.sessions ?? []).map(session), window: normalizeSessionListWindow(response.window) };
  },
  get: async (id, options) => session(await api.get(id, options)),
  create: async (input, options) => session(await api.create({ ...defaults, ...input }, options)),
  update: async (id, input, options) => session(await api.update(id, input, options)),
  ...(api.lifecycle ? { archive: async (id: string, options?: { signal?: AbortSignal }) => session(await api.lifecycle!(id, 'archive', options)) } : {}),
});
const messageWindow = (window: WireMessageWindow = {}) => ({
  mode: window.mode ?? 'full', hasOlder: window.hasOlder ?? window.has_older ?? false,
  nextBefore: window.nextBefore ?? window.next_before ?? '', limit: window.limit ?? null,
  payloadMode: window.payloadMode ?? window.payload_mode ?? 'full',
});
const messageOverrides = (api: MessageAPI): Pick<ChatTransport['messages'], 'list'> => ({
  list: async (id, input = {}, options) => {
    const response = await api.list(id, { ...input, payload_mode: input.payloadMode }, options);
    return { messages: (response.messages ?? []).map(normalizeMessageRecord), window: messageWindow(response.window) };
  },
});
export const createHostTransport = (options: Pick<HostOptions, 'sessionAPI' | 'messageAPI' | 'operationDefinitions'>, configuration: HostConfiguration): ChatTransport => {
  const base = createChatTransport({ ...configuration,
    operationDefinitions: options.operationDefinitions ?? createHostOperationDefinitions(),
  });
  return {
    ...base,
    ...(options.sessionAPI ? { sessions: sessionOverrides(options.sessionAPI, { ...configuration.listDefaults, ...configuration.sessionDefaults }) } : {}),
    ...(options.messageAPI ? { messages: { ...base.messages, ...messageOverrides(options.messageAPI) } } : {}),
  };
};
